import { useCallback, useEffect, useState } from 'react';
import type { ActivityEntry, ActivitySummary } from '../../../services/src/shared/api-types.ts';
import type { ScreenProps } from '../App.tsx';
import { body, headline, isFailure } from '../activity.ts';
import { money, num, stamp } from '../format.ts';
import { Empty, ErrorBanner, Loading, Stat } from '../ui.tsx';

export function Activity({ api }: ScreenProps) {
  const [items, setItems] = useState<ActivityEntry[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (before?: string) => {
      setBusy(true);
      setError(null);
      return api.activity(before).then(
        (r) => {
          setItems((prev) => (before ? [...prev, ...r.items] : r.items));
          if (r.summary) setSummary(r.summary);
          setNext(r.next);
          setLoaded(true);
          setBusy(false);
        },
        (e: Error) => {
          setError(e.message);
          setBusy(false);
        },
      );
    },
    [api],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Activity</h1>
          <p className="lede">Everything posted to Xero, and who approved what.</p>
        </div>
      </header>

      {summary && (
        <div className="stats">
          <Stat label="Posted to Xero" value={num(summary.posted)} note="last 7 days" />
          <Stat label="Approved by people" value={num(summary.approved)} note="last 7 days" />
          <Stat label="Rejected" value={num(summary.rejected)} note="last 7 days" />
        </div>
      )}

      {error && <ErrorBanner message={error} onRetry={() => void load(loaded ? (next ?? undefined) : undefined)} />}
      {!loaded && !error && <Loading kind="list" />}
      {loaded && items.length === 0 && <Empty>Nothing yet.</Empty>}
      {items.length > 0 && (
        <ol className="card timeline">
          {items.map((a) => (
            <li key={a.id}>
              <time className="when" dateTime={a.at}>
                {stamp(a.at)}
              </time>
              <span className="what">
                <strong className={isFailure(a) ? 'st-err' : undefined}>{headline(a)}</strong>
                {body(a) && ` · ${body(a)}`}
              </span>
              {a.amount !== undefined && <span className="num">{money(a.amount)}</span>}
            </li>
          ))}
        </ol>
      )}
      {next && (
        <div className="load-more">
          <button type="button" className="btn" disabled={busy} onClick={() => void load(next)}>
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
